import axios from "axios";
import { logout } from "../utils/auth";

const api = axios.create({ baseURL: process.env.REACT_APP_BASE_URL });

// Kept separate from `api` so a failed refresh cannot enter this interceptor
// again and create a refresh loop.
const refreshClient = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

let refreshRequest = null;
let logoutInProgress = false;

const readStoredJson = (key) => {
  const rawValue = localStorage.getItem(key);
  if (!rawValue) return null;

  try {
    return JSON.parse(rawValue);
  } catch (_error) {
    return null;
  }
};

const logoutExpiredSession = () => {
  if (logoutInProgress) return;

  logoutInProgress = true;
  delete api.defaults.headers.common.Authorization;
  logout();
};

const missingSessionError = () => {
  const error = new Error("Missing refresh session data");
  error.code = "MISSING_REFRESH_SESSION";
  return error;
};

const invalidRefreshResponseError = () => {
  const error = new Error("Refresh response does not contain an access token");
  error.code = "INVALID_REFRESH_RESPONSE";
  return error;
};

const shouldLogoutAfterRefreshError = (error) => {
  if (
    error?.code === "MISSING_REFRESH_SESSION" ||
    error?.code === "INVALID_REFRESH_RESPONSE"
  ) {
    return true;
  }

  // Network and server errors are not proof that the user session is invalid.
  // Keep the session in that case so a temporary outage does not log out users.
  return [400, 401, 403].includes(error?.response?.status);
};

const refreshAccessToken = () => {
  if (refreshRequest) return refreshRequest;

  const refreshToken = localStorage.getItem("refreshToken");
  const user = readStoredJson("user");

  if (!refreshToken || !user?.email || !user?.role || !user?.id) {
    return Promise.reject(missingSessionError());
  }

  const headers = {};
  const language = localStorage.getItem("local");
  if (language) headers["Accept-Language"] = language;

  refreshRequest = refreshClient
    .post(
      "/refresh",
      {
        email: user.email,
        role: user.role,
        uid: user.id,
        refreshToken,
      },
      { headers },
    )
    .then((response) => {
      const token = response.data?.token;
      if (!token) throw invalidRefreshResponseError();

      localStorage.setItem("token", JSON.stringify({ token }));
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      return token;
    })
    .finally(() => {
      refreshRequest = null;
    });

  return refreshRequest;
};

api.interceptors.request.use(
  (config) => {
    const userToken = readStoredJson("token");
    const language = localStorage.getItem("local");

    if (userToken?.token) {
      config.headers.Authorization = `Bearer ${userToken.token}`;
    }

    if (language) {
      config.headers["Accept-Language"] = language;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isMissingLocalUser =
      error.response?.status === 501 &&
      error.response?.data?.message === "no_user";

    // A browser can retain a valid token from another local database or from
    // the hosted application. The local API correctly rejects it because its
    // user no longer exists. Clear this stale session before page components
    // retry protected requests and produce uncaught Axios errors.
    if (isMissingLocalUser) {
      logoutExpiredSession();
      // Navigation to /login is synchronous from the browser perspective;
      // keep the obsolete requests quiet while that navigation is occurring.
      return new Promise(() => {});
    }

    if (error.response?.status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    // A retried request still receiving 401 cannot be recovered safely.
    if (originalRequest._retry) {
      logoutExpiredSession();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const token = await refreshAccessToken();
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return api(originalRequest);
    } catch (refreshError) {
      if (shouldLogoutAfterRefreshError(refreshError)) {
        logoutExpiredSession();
      }
      return Promise.reject(refreshError);
    }
  },
);

export default api;
